// src/utils.ts

/**
 * Generates a unique session ID based on the current timestamp.
 * @returns A string representing the session ID.
 */
export const generateSessionId = (): string => `session_${Date.now()}`;

/**
 * Extracts the location (first word) from an event string.
 * @param eventString - The string describing the event.
 * @returns The extracted location or the original string if no match.
 */
export function extractLocation(eventString: string): string {
  if (!eventString) return '';
  // Extract first word before any parenthesis or space
  const match = eventString.match(/^([^\s(]+)/);
  return match ? match[1] : eventString;
}

/**
 * Represents the result of formatting a countdown.
 */
export interface CountdownResult {
  text: string;
  className: string;
}

/**
 * Formats the countdown time to a race start.
 * @param startTimeString - The ISO string of the race start time.
 * @returns An object with the formatted text and a CSS class name.
 */
export const formatCountdown = (startTimeString?: string): CountdownResult => {
  if (!startTimeString) return { text: 'N/A', className: '' };

  const startTime = new Date(startTimeString);
  const now = new Date();

  const timeDiff = startTime.getTime() - now.getTime();

  if (timeDiff < 0) {
    return { text: 'Started', className: 'countdown-started' };
  }

  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  let text = '';
  if (hours > 0) {
    text = `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    text = `${minutes}m ${seconds}s`;
  } else {
    text = `${seconds}s`;
  }

  let className = '';
  if (timeDiff < 5 * 60 * 1000) {
    className = 'countdown-imminent';
  } else if (timeDiff < 30 * 60 * 1000) {
    className = 'countdown-soon';
  } else if (timeDiff < 60 * 60 * 1000) {
    className = 'countdown-approaching';
  } else {
    className = 'countdown-distant';
  }

  return { text, className };
};

/**
 * Helper to calculate and format change between two values.
 * @param currentValue - The current value.
 * @param previousValue - The previous value.
 * @returns A string representing the formatted change, or null.
 */
export const formatChange = (
  currentValue?: string | number | null,
  previousValue?: string | number | null
): string | null => {
  if (
    previousValue === undefined ||
    previousValue === null ||
    currentValue === undefined ||
    currentValue === null
  ) {
    return null;
  }
  const current = parseFloat(String(currentValue));
  const previous = parseFloat(String(previousValue));
  if (isNaN(current) || isNaN(previous)) return null;
  const change = parseFloat((current - previous).toFixed(4));
  if (Math.abs(change) < 0.005) return null; // Consider negligible changes as null
  if (change > 0) {
    return `+${change
      .toFixed(Math.abs(change) < 0.1 ? 2 : 2) // Dynamic precision
      .replace(/\.?0+$/, '')}`; // Remove trailing zeros
  } else {
    return `${change
      .toFixed(Math.abs(change) < 0.1 ? 2 : 2) // Dynamic precision
      .replace(/\.?0+$/, '')}`; // Remove trailing zeros
  }
};

/**
 * Helper to determine which class to use for change indicators.
 * @param field - The field name (e.g., 'backOdds', 'layOdds').
 * @param change - The formatted change string (e.g., '+0.1', '-0.05').
 * @returns A CSS class string.
 */
export const getChangeIndicatorClass = (
  field: string,
  change: string | null
): string => {
  if (!change) return '';
  const isPositive = change.startsWith('+');
  if (field === 'backOdds' || field === 'combinedBackOdds') {
    return isPositive ? 'change-up-back' : 'change-down-back';
  } else if (field === 'layOdds' || field === 'combinedLayOdds') {
    return isPositive ? 'change-up-lay' : 'change-down-lay';
  }
  return '';
};

/**
 * Helper to get flash class for a cell based on value change.
 * @param field - The field name.
 * @param currentValue - The current value.
 * @param previousValue - The previous value.
 * @returns A CSS class string for flash animation.
 */
export function getFlashClass(
  field: string,
  currentValue?: string | number | null,
  previousValue?: string | number | null
): string {
  const changeAmount = formatChange(currentValue, previousValue);
  if (!changeAmount) return '';
  // Assumes CSS classes like 'flash-up-back', 'flash-down-lay' exist
  return getChangeIndicatorClass(field, changeAmount).replace(
    'change-',
    'flash-'
  );
}

/**
 * Checks if a race has started based on its start time.
 * @param startTimeString - The ISO string of the race start time.
 * @returns True if the race has started, false otherwise.
 */
export const hasRaceStarted = (startTimeString?: string): boolean => {
  if (!startTimeString) return false;
  const startTime = new Date(startTimeString);
  const now = new Date();
  return startTime < now;
};

/**
 * Gets the color and animation styles for countdown text based on time remaining.
 * @param startTimeString - The ISO string of the race start time.
 * @returns An object with color style and className for animations.
 */
export const getCountdownColor = (
  startTimeString?: string
): { color: string; className: string } => {
  if (!startTimeString) return { color: '', className: '' };

  const startTime = new Date(startTimeString);
  const now = new Date();
  const timeDiff = startTime.getTime() - now.getTime();

  if (timeDiff < 0) {
    return { color: '', className: '' }; // Race has started
  }

  // Less than 10 minutes: red and pulsing
  if (timeDiff < 10 * 60 * 1000) {
    return {
      color: '#EF4444',
      className: 'animate-pulse',
    };
  }

  // Less than 30 minutes: orange
  if (timeDiff < 30 * 60 * 1000) {
    return {
      color: '#E87E37',
      className: '',
    };
  }

  // Less than 1 hour: green
  if (timeDiff < 60 * 60 * 1000) {
    return {
      color: '#57C785',
      className: '',
    };
  }

  // More than 1 hour: default color
  return { color: '', className: '' };
};

/**
 * Determines the liquidity symbol based on the liquidity amount.
 * @param liquidity - The liquidity amount.
 * @returns An object with the symbol string (e.g., "$", "$$", "$$$") and a numerical value.
 */
export const getLiquiditySymbol = (
  liquidity?: number
): { symbol: string; value: number } => {
  if (!liquidity || liquidity < 1000) return { symbol: '$', value: 1 };
  if (liquidity < 5000) return { symbol: '$$', value: 2 };
  return { symbol: '$$$', value: 3 };
};
